import { useEffect } from 'react';

import { usePublicLinkPageDataProvider } from '@confluence/public-links/entry-points/usePublicLinkPageDataProvider';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import type { ContentMode } from '@confluence/content-types-utils/entry-points/getContentMode';
import { fg } from '@confluence/feature-gating';

import { useEditorObjectHeaderContext } from './EditorObjectHeaderContext';

export type DisableEditorActionsData = {
	shouldDisableEditorButtons: boolean;
	isPublishDisabledForPublishModal: boolean;
	isPublishDisabled: boolean;
	pageIsExternallyShared: boolean;
	isCloseButtonDisabled?: boolean;
};

/**
 * This hook is used to determine what elements should be disabled for editor
 */
export const useDisableEditorActions = (
	contentId: string,
	contentMode: ContentMode | undefined,
): DisableEditorActionsData => {
	const isEditorMode = contentMode === 'draft' || contentMode === 'edit' || contentMode === 'live';

	// This is only used to disable the buttons in the editor
	const publicLinkData = usePublicLinkPageDataProvider({
		pageId: contentId,
		fetchPolicy: 'cache-and-network',
		skip: !isEditorMode,
	});

	// Data is not used so we don't worry about errors here
	useEffect(() => {
		if (!isEditorMode) {
			return;
		}
		for (const error of publicLinkData.errors) {
			markErrorAsHandled(error);
		}
	}, [isEditorMode, publicLinkData.errors]);

	const {
		isPublishingContent,
		userOffline,
		forceDisabled,
		editorTitle,
		collabLimitReached,
		collabEditConnected,
	} = useEditorObjectHeaderContext();

	if (!isEditorMode) {
		return {
			shouldDisableEditorButtons: false,
			isPublishDisabledForPublishModal: false,
			isPublishDisabled: false,
			pageIsExternallyShared: false,
		};
	}

	const publicLinkDataIsLoading = publicLinkData.isLoading;
	const pageIsExternallyShared =
		!publicLinkData.isLoading && publicLinkData.isPublicLinkFullyEnabled;

	const shouldDisableActions = isPublishingContent || userOffline || !!forceDisabled;

	// this check should be equivalent to areMainButtonsDisabled
	const shouldDisableEditorButtons = shouldDisableActions || publicLinkDataIsLoading;

	const isEditorTitleEmpty = !editorTitle || !editorTitle.trim();
	const isPublishDisabledForPublishModal = fg('cc_publish_update_button_no_initial_disabled_state')
		? shouldDisableActions || Boolean(collabLimitReached)
		: shouldDisableEditorButtons || Boolean(collabLimitReached) || !collabEditConnected;

	const isPublishDisabled = isEditorTitleEmpty || isPublishDisabledForPublishModal;

	return {
		shouldDisableEditorButtons,
		isPublishDisabledForPublishModal,
		isPublishDisabled,
		isCloseButtonDisabled: shouldDisableActions,
		pageIsExternallyShared,
	};
};
